import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, X } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useCart } from "../lib/cart";
import { useSiteConfig } from "../lib/siteConfig";

// Cart page — server-side priced totals via /api/cart/validate.
// Checkout button hits /api/checkout/session which returns 503 until Stripe
// keys are configured, at which point payments will flow through Stripe.
export default function CartPage() {
  const { lines, totals, update, remove, discountToken } = useCart();
  const { products_available } = useSiteConfig();
  const [checkoutErr, setCheckoutErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { document.title = "Cart · Philadelphia Freeway"; }, []);

  const checkout = async () => {
    setLoading(true); setCheckoutErr(null);
    try {
      const res = await fetch(`${API}/api/checkout/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lines, discount_token: discountToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Checkout is not available yet.");
      if (data.url) window.location.href = data.url;
    } catch (err) { setCheckoutErr(err.message); } finally { setLoading(false); }
  };

  // TEMPORARY availability mode: while `products_available` is false the
  // cart page renders the empty/coming-soon state (no checkout attempt is
  // possible from the UI). All commerce plumbing below remains wired.
  if (!products_available) {
    return (
      <div className="relative bg-white min-h-screen text-black flex flex-col" style={{ background: "white" }}>
        <Navbar />
        <main className="flex-1">
          <div className="max-w-[900px] mx-auto px-4 md:px-10 pt-28 md:pt-36 pb-24 text-center">
            <h1 data-testid="cart-coming-soon-heading" className="font-wide uppercase text-4xl md:text-5xl">Cart</h1>
            <div data-testid="cart-coming-soon" className="mt-10 border-y border-black/15 py-14">
              <div className="font-wide uppercase text-2xl">Online Drop Coming Soon</div>
              <p className="mt-3 font-body text-black/70 text-sm md:text-base max-w-md mx-auto">
                Products are not yet available for online purchase. Join the newsletter to save 10% and be first when the drop opens.
              </p>
              <Link
                to="/"
                data-testid="cart-back-home"
                className="mt-8 inline-block bg-black text-white font-display uppercase text-sm tracking-[0.22em] px-8 py-3 hover:bg-[#F4C10A] hover:text-black transition-colors"
              >
                Shop the Collection
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative bg-white min-h-screen text-black flex flex-col" style={{ background: "white" }}>
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1200px] mx-auto px-4 md:px-10 pt-28 md:pt-36 pb-16 md:pb-24">
          <h1 className="font-wide uppercase text-4xl md:text-5xl">Cart</h1>
          {(!lines.length) ? (
            <div data-testid="cart-empty" className="mt-12 border-y border-black/15 py-16 text-center">
              <div className="font-wide uppercase text-2xl">Your cart is empty</div>
              <Link to="/" className="mt-6 inline-block bg-black text-white font-display uppercase text-sm tracking-[0.22em] px-8 py-3 hover:bg-[#F4C10A] hover:text-black">Shop the Collection</Link>
            </div>
          ) : (
            <div className="mt-8 grid md:grid-cols-[1fr_360px] gap-10">
              <div data-testid="cart-lines" className="divide-y divide-black/10 border-y border-black/10">
                {(totals.lines?.length ? totals.lines : lines).map((l) => (
                  <div key={`${l.slug}-${l.size}`} className="py-5 flex gap-4 items-start" data-testid={`cart-line-${l.slug}`}>
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-neutral-100 flex-shrink-0">
                      {l.image && <img src={l.image} alt="" className="w-full h-full object-contain p-2"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-sm leading-tight">{l.name || l.slug}</div>
                      {l.size && <div className="mt-1 font-mono text-[11px] tracking-[0.2em] uppercase text-black/60">Size · {l.size}</div>}
                      <div className="mt-3 flex items-center gap-3">
                        <div className="inline-flex items-center border border-black/25">
                          <button onClick={() => update(l.slug, l.size, l.quantity-1)} aria-label="dec" className="p-1.5 hover:bg-black/5"><Minus className="w-3.5 h-3.5"/></button>
                          <div className="px-3 font-mono text-sm" data-testid={`cart-qty-${l.slug}`}>{l.quantity}</div>
                          <button onClick={() => update(l.slug, l.size, l.quantity+1)} aria-label="inc" className="p-1.5 hover:bg-black/5"><Plus className="w-3.5 h-3.5"/></button>
                        </div>
                        <button onClick={() => remove(l.slug, l.size)} aria-label="remove" data-testid={`cart-remove-${l.slug}`} className="text-black/50 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                      </div>
                    </div>
                    <div className="font-mono text-sm text-right">${(l.line_total ?? (l.unit_price ?? 0) * l.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <aside className="bg-neutral-50 p-6 md:p-7 h-fit" data-testid="cart-summary">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-4">Order Summary</div>
                <div className="flex justify-between text-sm py-2"><span>Subtotal</span><span data-testid="cart-subtotal">${totals.subtotal.toFixed(2)}</span></div>
                {totals.discount_valid && (
                  <div className="flex justify-between text-sm py-2 text-[#B48A00]" data-testid="cart-discount"><span>Signup Discount (10%)</span><span>−${totals.discount_amount.toFixed(2)}</span></div>
                )}
                <div className="mt-3 pt-3 border-t border-black/15 flex justify-between font-wide uppercase text-lg" data-testid="cart-total"><span>Total</span><span>${totals.total.toFixed(2)}</span></div>
                <button onClick={checkout} disabled={loading} data-testid="cart-checkout"
                  className="mt-6 w-full bg-black text-white font-display uppercase text-sm tracking-[0.22em] py-4 hover:bg-[#F4C10A] hover:text-black transition-colors disabled:opacity-50">
                  {loading ? "Preparing…" : "Checkout"}
                </button>
                {checkoutErr && <div data-testid="cart-checkout-error" className="mt-3 text-red-600 text-sm">{checkoutErr}</div>}
                <div className="mt-5 font-mono text-[10px] tracking-[0.3em] uppercase text-black/50 text-center">Final Sale · No Returns or Exchanges</div>
              </aside>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// Little floating cross-sell toast that appears after an add-to-cart action.
export function CartCrossSell() {
  const { showToast, setShowToast, add } = useCart();
  const [rec, setRec] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!showToast) { setRec(null); return; }
    fetch(`${API}/api/products`).then(r => r.json()).then((all) => {
      const target = showToast.category === "apparel" ? "headwear" : "apparel";
      // Prefer same collection as added item.
      const sameCol = all.filter(p => p.category === target && p.collection === showToast.collection);
      setRec((sameCol[0]) || all.find(p => p.category === target));
    });
  }, [showToast, API]);

  if (!showToast || !rec) return null;
  const message = showToast.category === "apparel" ? "Others who bought this item also bought a hat" : "Others also bought a tee";
  return (
    <div data-testid="cross-sell-toast" className="fixed bottom-4 right-4 z-[90] max-w-xs md:max-w-sm bg-white text-black shadow-2xl border border-black/10">
      <button onClick={() => setShowToast(null)} aria-label="dismiss" className="absolute top-2 right-2 text-black/50 hover:text-black"><X className="w-4 h-4"/></button>
      <div className="p-5">
        <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#B48A00]">Recommended</div>
        <div className="mt-1 font-display uppercase text-sm leading-tight">{message}</div>
        <Link to={`/product/${rec.slug}`} onClick={() => setShowToast(null)}
          data-testid="cross-sell-link"
          className="mt-3 flex items-center gap-3 hover:bg-neutral-50 p-2 -m-2">
          <div className="w-14 h-14 bg-neutral-100 flex-shrink-0"><img src={rec.images[0]} alt="" className="w-full h-full object-contain p-1"/></div>
          <div className="min-w-0">
            <div className="font-display uppercase text-xs leading-tight line-clamp-2">{rec.name}</div>
            <div className="mt-0.5 font-mono text-xs">${rec.price.toFixed(2)}</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
